import { Close, SearchLocate } from "@carbon/icons-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageLoader from "../../components/PageLoader";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import OrderHeader from "../../components/orderHeader/OrderHeader";
import { useHeader } from "../../contexts/PageHeaderContext";
import useInboundDeliveryByIdQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveryByIdQuery";
import {
  InboundDeliveryFragment,
  InboundDeliveryStatus,
  TimeSlotFragment,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../../models/deliveryItem";
import { isNullOrEmpty } from "../../utils/helper";
import { mapToIDeliveryItems } from "../../utils/mapper";
import SelectItems from "./screens/selectItems/SelectItems";
import TimeSlotSelection from "./screens/timeSlotSelection/TimeSlotSelection";

interface BookTimeSlotProps {}

const BookTimeSlot = (props: BookTimeSlotProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { updateHeader, updateSecondHeader } = useHeader();

  const [deliveryItems, setDeliveryItems] = useState<IDeliveryItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<IDeliveryItem[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlotFragment[] | null>(null);

  const { loading, error } = useInboundDeliveryByIdQuery({
    id: idFromUrl,
    dataAction: (inboundDelivery) => {
      // We want to filter out items that already have a time slot booked
      const filteredItems = inboundDelivery.items.filter(
        (item) =>
          item.__typename === "MaterialItem" && item.timeSlotResolved === null,
      );

      setDeliveryItems(mapToIDeliveryItems(filteredItems));
      updateHeaders(inboundDelivery);
    },
    errorAction: () => {
      updateHeader(<>{t("delivery")}</>);
    },
  });

  const updateHeaders = (inboundDelivery: InboundDeliveryFragment) => {
    const { deliveryNumber } = inboundDelivery;

    updateHeader(<>{t("bookTimeSlot")}</>);
    updateSecondHeader(<> {`${t("deliveryNumber")}: ${deliveryNumber}`}</>);
  };

  const renderContent = () => {
    if (isNullOrEmpty(idFromUrl))
      return (
        <FallbackComponent middleIcon={<SearchLocate />} text={t("idEmpty")} />
      );

    if (loading) return <PageLoader />;

    if (error)
      return (
        <FallbackComponent
          middleIcon={<Close size={32} />}
          text={`${t("queryError")}. ${t("tryAgainLater")}`}
        />
      );

    return !timeSlots ? (
      <SelectItems
        deliveryItems={deliveryItems}
        selectedDeliveryItems={selectedItems}
        setSelectedDeliveryItems={setSelectedItems}
        setTimeSlots={setTimeSlots}
      />
    ) : (
      <TimeSlotSelection
        timeSlots={timeSlots}
        selectedDeliveryItems={selectedItems}
      />
    );
  };

  return (
    <div className="page-content">
      <Helmet title={t("bookTimeSlot")} />
      <OrderHeader
        paddingBottom="16px"
        amount={deliveryItems.length}
        status={InboundDeliveryStatus.Open}
      />
      {renderContent()}
    </div>
  );
};
export default BookTimeSlot;
